// A state's own properties, kept separate from the requirements to reach it.
// "A draft only shows on the Orders screen" is this, not a rule about a move.
import { cn } from '@my/ui';
import {
  invoiceableFrom,
  isInvoiceableFloor,
  type StateBehaviour,
  type Workflow,
  type WorkflowState,
} from '../lib/mockWorkflow';

interface Flag {
  key: keyof StateBehaviour;
  label: string;
  detail: string;
  /** Order tracks only — a trip and a leg are never invoiced. */
  orderOnly?: boolean;
}

const FLAGS: Flag[] = [
  {
    key: 'plannable',
    label: 'Appears in planning',
    detail: 'Shows in the plan cockpit and the manifest queues.',
  },
  {
    key: 'editable',
    label: 'Still editable',
    detail: 'Operators can change the order while it sits here.',
  },
  {
    key: 'invoiceable',
    // Filled in with the current threshold — see `label` below. The row states
    // the whole rule rather than just this state's share of it, because the
    // setting IS one threshold across the chain.
    label: 'Appears in invoicing',
    // Saying "credit customers only" on the row matters: read as "all
    // customers", switching it off would look like it stops a cash order being
    // billed — which it cannot, because the prepay rule forces cash to be
    // billable from Open.
    detail:
      'Credit customers only. Cash is always invoiceable from Open, since it has to be paid then.',
    orderOnly: true,
  },
];

export function StateBehaviourCard({
  workflow,
  state,
  onChange,
  onChangeInvoiceableFrom,
}: {
  workflow: Workflow;
  state: WorkflowState;
  onChange: (next: WorkflowState) => void;
  /**
   * Separate from `onChange` because this flag carries a consequence through
   * the chain (see `withInvoiceableFrom`) — it cannot be expressed as a change
   * to one state.
   */
  onChangeInvoiceableFrom: (key: string, value: boolean) => void;
}) {
  // The final state is a floor: a finished order must always be billable, so
  // its switch is locked on rather than merely defaulted on.
  const atFloor = isInvoiceableFloor(workflow, state.key);
  const from = invoiceableFrom(workflow);

  const toggle = (key: keyof StateBehaviour) => {
    if (key === 'invoiceable') {
      if (atFloor) return;
      onChangeInvoiceableFrom(state.key, state.behaviour.invoiceable !== true);
      return;
    }
    onChange({ ...state, behaviour: { ...state.behaviour, [key]: !state.behaviour[key] } });
  };

  const flags = FLAGS.filter((f) => !f.orderOnly || workflow.subject === 'order');

  return (
    <div className="flex h-full flex-col rounded-lg border border-slate-200 bg-white">
      <div className="border-b border-slate-200 px-4 py-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          While {state.label}
        </h3>
      </div>
      <ul className="flex-1 divide-y divide-slate-100">
        {flags.map((f) => {
          const on = state.behaviour[f.key] === true;
          const isInvoicing = f.key === 'invoiceable';
          const locked = isInvoicing && atFloor;
          const label =
            isInvoicing && from ? `${f.label} from ${from.label}` : f.label;
          return (
            <li key={f.key}>
              <button
                type="button"
                disabled={locked}
                title={
                  locked
                    ? 'A finished order must always be invoiceable — this cannot be switched off.'
                    : undefined
                }
                onClick={() => toggle(f.key)}
                className={cn(
                  'flex w-full items-start gap-2.5 px-4 py-2.5 text-left',
                  locked ? 'cursor-default' : 'hover:bg-slate-50',
                )}
              >
                {/* A switch rather than a tick: these are settings to change,
                    not facts to read. */}
                <span
                  className={cn(
                    'mt-0.5 flex h-4 w-7 shrink-0 items-center rounded-full p-0.5 transition-colors',
                    on ? 'bg-emerald-500' : 'bg-slate-300',
                  )}
                >
                  <span
                    className={cn(
                      'h-3 w-3 rounded-full bg-white transition-transform',
                      on && 'translate-x-3',
                    )}
                  />
                </span>
                <span>
                  <span className={on ? 'text-sm text-slate-800' : 'text-sm text-slate-400'}>
                    {label}
                  </span>
                  <span className="block text-[11px] text-slate-400">{f.detail}</span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
